'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';

const programs = [
  {
    id: 'education',
    title: 'Education Program', titleKn: 'ಶಿಕ್ಷಣ ಕಾರ್ಯಕ್ರಮ', icon: '📖',
    color: 'from-saffron to-saffron-dark', bg: 'bg-saffron-pale', border: 'border-saffron/20', accent: 'text-saffron',
    desc: 'Our flagship education program bridges learning gaps for underprivileged children through structured after-school programs, digital literacy, and mentorship. We operate 15 learning centers across Bengaluru, serving children from Classes 1–10.',
    descKn: 'ನಮ್ಮ ಪ್ರಮುಖ ಶಿಕ್ಷಣ ಕಾರ್ಯಕ್ರಮವು ರಚನಾತ್ಮಕ ಶಾಲೆಯ ನಂತರದ ಕಾರ್ಯಕ್ರಮಗಳು, ಡಿಜಿಟಲ್ ಸಾಕ್ಷರತೆ ಮತ್ತು ಮಾರ್ಗದರ್ಶನದ ಮೂಲಕ ಅಸಹಾಯಕ ಮಕ್ಕಳಿಗೆ ಕಲಿಕೆಯ ಅಂತರ ಕಡಿಮೆ ಮಾಡುತ್ತದೆ.',
    metrics: [{ value: '15', label: 'Learning Centers', labelKn: 'ಕಲಿಕಾ ಕೇಂದ್ರಗಳು' }, { value: '8,500+', label: 'Students Enrolled', labelKn: 'ನೋಂದಾಯಿತ ವಿದ್ಯಾರ್ಥಿಗಳು' }, { value: '92%', label: 'Pass Rate', labelKn: 'ಉತ್ತೀರ್ಣ ದರ' }, { value: '3 Districts', label: 'Coverage', labelKn: 'ವ್ಯಾಪ್ತಿ' }],
    activities: ['After-school tutoring', 'Digital literacy classes', 'Library & reading programs', 'STEM workshops', 'Career counseling'],
    activitiesKn: ['ಶಾಲೆಯ ನಂತರದ ಟ್ಯೂಟರಿಂಗ್', 'ಡಿಜಿಟಲ್ ಸಾಕ್ಷರತೆ ತರಗತಿಗಳು', 'ಗ್ರಂಥಾಲಯ ಮತ್ತು ಓದುವ ಕಾರ್ಯಕ್ರಮಗಳು', 'STEM ಕಾರ್ಯಾಗಾರಗಳು', 'ವೃತ್ತಿ ಸಲಹೆ'],
  },
  {
    id: 'scholarships',
    title: 'Scholarships', titleKn: 'ವಿದ್ಯಾರ್ಥಿವೇತನ', icon: '🏆',
    color: 'from-crimson to-crimson-dark', bg: 'bg-crimson-pale', border: 'border-crimson/20', accent: 'text-crimson',
    desc: 'Merit and need-based scholarships enabling deserving students from low-income families to pursue higher education — from Class 11 through professional degrees — without financial barriers.',
    descKn: 'ಕಡಿಮೆ ಆದಾಯದ ಕುಟುಂಬಗಳ ಅರ್ಹ ವಿದ್ಯಾರ್ಥಿಗಳಿಗೆ ಆರ್ಥಿಕ ಅಡೆತಡೆಗಳಿಲ್ಲದೆ ಉನ್ನತ ಶಿಕ್ಷಣ ಪಡೆಯಲು ಮೆರಿಟ್ ಮತ್ತು ಅಗತ್ಯ ಆಧಾರಿತ ವಿದ್ಯಾರ್ಥಿವೇತನಗಳು.',
    metrics: [{ value: '850+', label: 'Scholarships Awarded', labelKn: 'ವಿದ್ಯಾರ್ಥಿವೇತನಗಳು' }, { value: '₹2.5 Cr+', label: 'Disbursed', labelKn: 'ವಿತರಿಸಲಾಗಿದೆ' }, { value: '78%', label: 'First-Gen Graduates', labelKn: 'ಮೊದಲ ತಲೆಮಾರಿನ ಪದವೀಧರರು' }, { value: '12 Streams', label: 'Covered', labelKn: 'ಒಳಗೊಂಡಿದೆ' }],
    activities: ['Pre-university scholarships', 'Engineering & medical support', 'Arts & commerce grants', 'Vocational training support', 'Mentorship pairing'],
    activitiesKn: ['ಪೂರ್ವ-ವಿಶ್ವವಿದ್ಯಾಲಯ ವಿದ್ಯಾರ್ಥಿವೇತನ', 'ಎಂಜಿನಿಯರಿಂಗ್ ಮತ್ತು ವೈದ್ಯಕೀಯ ಬೆಂಬಲ', 'ಕಲೆ ಮತ್ತು ವಾಣಿಜ್ಯ ಅನುದಾನ', 'ವೃತ್ತಿಪರ ತರಬೇತಿ ಬೆಂಬಲ', 'ಮಾರ್ಗದರ್ಶನ ಜೋಡಣೆ'],
  },
  {
    id: 'teacher-training',
    title: 'Teacher Training', titleKn: 'ಶಿಕ್ಷಕ ತರಬೇತಿ', icon: '🎯',
    color: 'from-indigo to-indigo-dark', bg: 'bg-indigo-pale', border: 'border-indigo/20', accent: 'text-indigo',
    desc: 'Upskilling government school teachers with modern pedagogy, technology integration, and child-centric teaching methods. Our certified trainers work directly in schools to ensure lasting impact.',
    descKn: 'ಆಧುನಿಕ ಶಿಕ್ಷಣ ವಿಧಾನ, ತಂತ್ರಜ್ಞಾನ ಏಕೀಕರಣ ಮತ್ತು ಮಕ್ಕಳ ಕೇಂದ್ರಿತ ಬೋಧನಾ ವಿಧಾನಗಳೊಂದಿಗೆ ಸರ್ಕಾರಿ ಶಾಲಾ ಶಿಕ್ಷಕರ ಕೌಶಲ್ಯ ಅಭಿವೃದ್ಧಿ.',
    metrics: [{ value: '200+', label: 'Teachers Trained', labelKn: 'ತರಬೇತಿ ಪಡೆದ ಶಿಕ್ಷಕರು' }, { value: '60+', label: 'Schools Partnered', labelKn: 'ಶಾಲೆ ಪಾಲುದಾರರು' }, { value: '25,000+', label: 'Students Benefited', labelKn: 'ಫಲಾನುಭವಿ ವಿದ್ಯಾರ್ಥಿಗಳು' }, { value: '4 Modules', label: 'Curriculum', labelKn: 'ಪಠ್ಯಕ್ರಮ' }],
    activities: ['Pedagogy workshops', 'Digital tools training', 'Classroom management', 'Inclusive education', 'Assessment design'],
    activitiesKn: ['ಶಿಕ್ಷಣ ವಿಧಾನ ಕಾರ್ಯಾಗಾರಗಳು', 'ಡಿಜಿಟಲ್ ಉಪಕರಣ ತರಬೇತಿ', 'ತರಗತಿ ನಿರ್ವಹಣೆ', 'ಸಮಗ್ರ ಶಿಕ್ಷಣ', 'ಮೌಲ್ಯಮಾಪನ ವಿನ್ಯಾಸ'],
  },
  {
    id: 'community-health',
    title: 'Community Health', titleKn: 'ಸಮುದಾಯ ಆರೋಗ್ಯ', icon: '❤️',
    color: 'from-gold to-gold-dark', bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-600',
    desc: 'Health camps, awareness drives, and preventive care programs reaching underserved communities. We focus on maternal health, child nutrition, sanitation, and mental health awareness.',
    descKn: 'ಆರೋಗ್ಯ ಶಿಬಿರಗಳು, ಜಾಗೃತಿ ಅಭಿಯಾನಗಳು ಮತ್ತು ತಡೆಗಟ್ಟುವ ಆರೈಕೆ ಕಾರ್ಯಕ್ರಮಗಳು ಅಸಹಾಯಕ ಸಮುದಾಯಗಳನ್ನು ತಲುಪುತ್ತವೆ.',
    metrics: [{ value: '45+', label: 'Communities Reached', labelKn: 'ತಲುಪಿದ ಸಮುದಾಯಗಳು' }, { value: '18,000+', label: 'Health Screenings', labelKn: 'ಆರೋಗ್ಯ ತಪಾಸಣೆಗಳು' }, { value: '120+', label: 'Health Camps', labelKn: 'ಆರೋಗ್ಯ ಶಿಬಿರಗಳು' }, { value: '8 Hospitals', label: 'Partner Network', labelKn: 'ಪಾಲುದಾರ ಜಾಲ' }],
    activities: ['Free health camps', 'Maternal & child health', 'Nutrition programs', 'Sanitation awareness', 'Mental health support'],
    activitiesKn: ['ಉಚಿತ ಆರೋಗ್ಯ ಶಿಬಿರಗಳು', 'ತಾಯಿ ಮತ್ತು ಮಕ್ಕಳ ಆರೋಗ್ಯ', 'ಪೋಷಣೆ ಕಾರ್ಯಕ್ರಮಗಳು', 'ನೈರ್ಮಲ್ಯ ಜಾಗೃತಿ', 'ಮಾನಸಿಕ ಆರೋಗ್ಯ ಬೆಂಬಲ'],
  },
  {
    id: 'women-youth',
    title: 'Women & Youth Empowerment', titleKn: 'ಮಹಿಳಾ ಮತ್ತು ಯುವ ಸಬಲೀಕರಣ', icon: '💪',
    color: 'from-purple-600 to-purple-800', bg: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-700',
    desc: 'Skill development, self-help groups, and livelihood programs empowering women and youth for economic independence. We run vocational training, entrepreneurship support, and leadership development.',
    descKn: 'ಕೌಶಲ್ಯ ಅಭಿವೃದ್ಧಿ, ಸ್ವಸಹಾಯ ಗುಂಪುಗಳು ಮತ್ತು ಜೀವನೋಪಾಯ ಕಾರ್ಯಕ್ರಮಗಳ ಮೂಲಕ ಮಹಿಳೆಯರು ಮತ್ತು ಯುವಕರ ಆರ್ಥಿಕ ಸ್ವಾತಂತ್ರ್ಯಕ್ಕಾಗಿ ಸಬಲೀಕರಣ.',
    metrics: [{ value: '1,200+', label: 'Women Empowered', labelKn: 'ಸಬಲೀಕೃತ ಮಹಿಳೆಯರು' }, { value: '85', label: 'Self-Help Groups', labelKn: 'ಸ್ವಸಹಾಯ ಗುಂಪುಗಳು' }, { value: '350+', label: 'Youth Trained', labelKn: 'ತರಬೇತಿ ಪಡೆದ ಯುವಕರು' }, { value: '60%', label: 'Employment Rate', labelKn: 'ಉದ್ಯೋಗ ದರ' }],
    activities: ['Vocational training', 'Self-help group formation', 'Entrepreneurship support', 'Legal rights awareness', 'Leadership programs'],
    activitiesKn: ['ವೃತ್ತಿಪರ ತರಬೇತಿ', 'ಸ್ವಸಹಾಯ ಗುಂಪು ರಚನೆ', 'ಉದ್ಯಮಶೀಲತೆ ಬೆಂಬಲ', 'ಕಾನೂನು ಹಕ್ಕುಗಳ ಜಾಗೃತಿ', 'ನಾಯಕತ್ವ ಕಾರ್ಯಕ್ರಮಗಳು'],
  },
];

export default function ProgramsPage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const kn = language === 'kn';

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <main className="pt-24">
        {/* Hero */}
        <section className="hero-gradient py-20 px-6 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm mb-8 ${kn ? 'font-kannada' : ''}`}>{t?.programs?.badge}</div>
            <h1 className={`text-4xl sm:text-5xl font-bold mb-4 ${kn ? 'font-kannada' : ''}`}>
              {t?.programs?.heroTitle} <span className="text-saffron-light">{t?.programs?.heroHighlight}</span>
            </h1>
            <p className={`text-white/75 text-lg max-w-2xl mx-auto ${kn ? 'font-kannada' : ''}`}>{t?.programs?.heroSubtitle}</p>
          </div>
        </section>

        {/* Programs */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6 space-y-16">
            {programs?.map((prog, i) => (
              <div key={prog?.id} id={prog?.id} className={`grid lg:grid-cols-2 gap-10 items-start ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={`rounded-3xl p-10 ${prog?.bg} border ${prog?.border}`}>
                  <div className="text-5xl mb-5">{prog?.icon}</div>
                  <h2 className={`text-2xl font-bold text-neutral-900 mb-5 ${kn ? 'font-kannada' : ''}`}>{kn ? prog?.titleKn : prog?.title}</h2>
                  <p className={`text-neutral-700 leading-relaxed ${kn ? 'font-kannada' : ''}`}>{kn ? prog?.descKn : prog?.desc}</p>
                </div>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    {prog?.metrics?.map((m) => (
                      <div key={m?.label} className="bg-white rounded-2xl p-5 shadow-card border border-neutral-100">
                        <div className={`text-2xl font-bold mb-1 ${prog?.accent}`}>{m?.value}</div>
                        <div className={`text-neutral-500 text-xs ${kn ? 'font-kannada' : ''}`}>{kn ? m?.labelKn : m?.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-neutral-50 rounded-2xl p-6">
                    <h4 className={`font-bold text-neutral-900 mb-4 text-sm uppercase tracking-wider ${kn ? 'font-kannada' : ''}`}>{t?.programs?.keyActivities}</h4>
                    <ul className="space-y-2">
                      {(kn ? prog?.activitiesKn : prog?.activities)?.map((act) => (
                        <li key={act} className={`flex items-center gap-3 text-sm text-neutral-700 ${kn ? 'font-kannada' : ''}`}>
                          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 bg-gradient-to-r ${prog?.color}`}></div>
                          {act}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link href="/donate" className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white bg-gradient-to-r ${prog?.color} ${kn ? 'font-kannada' : ''}`}>
                    {kn ? 'ಈ ಕಾರ್ಯಕ್ರಮವನ್ನು ಬೆಂಬಲಿಸಿ' : 'Support This Program'}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-saffron-pale">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className={`text-3xl font-bold text-indigo mb-4 ${kn ? 'font-kannada' : ''}`}>{t?.programs?.ctaTitle}</h2>
            <p className={`text-neutral-600 mb-8 ${kn ? 'font-kannada' : ''}`}>{t?.programs?.ctaSubtitle}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link href="/donate" className={`px-8 py-4 btn-saffron rounded-full font-bold text-base ${kn ? 'font-kannada' : ''}`}>{t?.programs?.donateBtn}</Link>
              <Link href="/get-involved" className={`px-8 py-4 btn-indigo rounded-full font-bold text-base ${kn ? 'font-kannada' : ''}`}>{t?.programs?.volunteerBtn}</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
